// 19f — the testable rules behind Settings · SLA policies.
//
// The artboard describes a match-ordered list: drag to reorder, conditions per
// row, first match wins. This system has none of that. `SlaPolicy` carries no
// condition set and no ordering column; a ticket gets a policy because a
// pipeline named one, or because a policy is flagged `isDefault`. Rendering
// drag handles and a "Plan = Enterprise" condition column would be inventing an
// engine that does not exist, and an admin would set a rule that never fires.
//
// So the screen keeps the artboard's ARGUMENT — say plainly what each policy
// actually applies to, and warn when something configured here will silently do
// nothing — and drops its mechanism. The rules for both live here.

import type { SlaPolicy } from '@centraoneplus/shared-types';
import type { PolicyConfigHealth } from '@services/sla.service';

/** What a policy actually governs, given how policies attach to tickets here. */
export type Reach =
  | 'default'          // every ticket, unless a pipeline names another
  | 'named'            // only where a pipeline names it
  | 'default-off'      // flagged default, but switched off
  | 'off';             // inactive and not the default

export const REACH_TEXT: Record<Reach, string> = {
  default: 'Every ticket, unless a pipeline names another',
  named: 'Only where a pipeline names it',
  'default-off': 'Nothing — it is the default but inactive',
  off: 'Nothing — inactive',
};

export function reachOf(policy: Pick<SlaPolicy, 'isDefault' | 'isActive'>): Reach {
  if (policy.isActive) return policy.isDefault ? 'default' : 'named';
  return policy.isDefault ? 'default-off' : 'off';
}

/**
 * A target, as minutes, rendered the way an admin set it.
 *
 * `undefined` and `0` both mean "no target", and both must read as such: a
 * policy showing "0m" would look like the strictest rule on the screen when it
 * is really the absence of one.
 */
export function targetText(minutes: number | undefined | null): string {
  if (!minutes || minutes < 0) return 'Not set';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return mins ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest ? `${days}d ${rest}h` : `${days}d`;
}

export type LadderState = 'none' | 'live' | 'untested' | 'inert' | 'broken';

export interface Ladder {
  state: LadderState;
  levels: number;
  /** One line, already phrased for the cell. */
  text: string;
}

/**
 * What this policy's escalation ladder will actually do.
 *
 * `configReport` is the only source that knows — it walks each stored rung and
 * reports whether it can ever fire, and whether it ever has. A row that says
 * "3 levels" without that is the failure mode the report was built for: a
 * ladder that reads as configured and escalates nothing.
 */
export function ladderOf(
  policy: Pick<SlaPolicy, 'id' | 'escalationRules'>,
  health: PolicyConfigHealth | undefined,
): Ladder {
  const levels = health?.levelCount ?? policy.escalationRules?.length ?? 0;
  if (levels === 0) return { state: 'none', levels: 0, text: 'No escalation' };
  if (!health) return { state: 'untested', levels, text: `${levels} levels · unchecked` };
  if (health.errorCount > 0) {
    return { state: 'broken', levels, text: `${levels} levels · ${health.errorCount} broken` };
  }
  if (health.inert) return { state: 'inert', levels, text: `${levels} levels · cannot fire` };
  if (health.neverExecuted) return { state: 'untested', levels, text: `${levels} levels · never fired` };
  return { state: 'live', levels, text: `${levels} levels · ${health.executionCount} fired` };
}

export type FindingTone = 'burgundy' | 'terracotta';

export interface Finding {
  id: string;
  tone: FindingTone;
  headline: string;
  body: string;
  /** Policy this finding is about, when it is about one. */
  policyId?: string;
}

/**
 * Everything configured on this screen that will silently do nothing.
 *
 * Ordered worst first: a tenant with no reachable default is losing SLA on
 * every unrouted ticket, which outranks a single broken ladder. Each finding
 * names the policy, because "something is wrong" sends an admin hunting.
 */
export function findings(
  policies: SlaPolicy[],
  report: { policies: PolicyConfigHealth[] } | undefined,
): Finding[] {
  const out: Finding[] = [];
  const health = new Map((report?.policies ?? []).map((p) => [p.policyId, p]));

  const activeDefault = policies.find((p) => p.isDefault && p.isActive);
  const inactiveDefault = policies.find((p) => p.isDefault && !p.isActive);

  if (!activeDefault && inactiveDefault) {
    out.push({
      id: 'default-inactive',
      tone: 'burgundy',
      policyId: inactiveDefault.id,
      headline: 'The default policy is switched off',
      body: `"${inactiveDefault.name}" is flagged as the default but is inactive, so a ticket that no pipeline routes gets no first-response or resolution target at all. Those tickets cannot breach, which also means they never appear in the SLA report.`,
    });
  } else if (!activeDefault && policies.length > 0) {
    out.push({
      id: 'no-default',
      tone: 'burgundy',
      headline: 'No policy is the default',
      body: 'A ticket only gets a target if a pipeline names a policy for it. Everything else is raised with no SLA — it cannot breach, and it is excluded from every compliance figure in Reports.',
    });
  }

  if (activeDefault && !activeDefault.firstResponseTarget && !activeDefault.resolutionTarget) {
    out.push({
      id: 'default-no-targets',
      tone: 'burgundy',
      policyId: activeDefault.id,
      headline: 'The default policy sets no targets',
      body: `"${activeDefault.name}" applies to every unrouted ticket but has neither a first-response nor a resolution target, so nothing it governs can ever breach.`,
    });
  }

  for (const policy of policies) {
    if (!policy.isActive) continue;
    const h = health.get(policy.id);
    if (!h) continue;
    if (h.errorCount > 0) {
      out.push({
        id: `broken-${policy.id}`,
        tone: 'burgundy',
        policyId: policy.id,
        headline: `"${policy.name}" has an escalation ladder that cannot run`,
        body: `${h.errorCount} of its ${h.levelCount} levels are misconfigured. The levels below a broken rung never execute, so the escalation this policy promises stops there.`,
      });
    } else if (h.inert) {
      out.push({
        id: `inert-${policy.id}`,
        tone: 'terracotta',
        policyId: policy.id,
        headline: `"${policy.name}" escalates to nobody`,
        body: `Its ${h.levelCount} levels are stored and valid but resolve to no recipient, so a breach on this policy notifies no one. It reads as configured on every screen that shows a level count.`,
      });
    }
  }

  return out;
}

export interface Summary {
  total: number;
  active: number;
  inactive: number;
  defaultName: string | null;
  businessHours: number;
}

export function summarise(policies: SlaPolicy[]): Summary {
  const active = policies.filter((p) => p.isActive);
  const def = active.find((p) => p.isDefault);
  return {
    total: policies.length,
    active: active.length,
    inactive: policies.length - active.length,
    defaultName: def?.name ?? null,
    businessHours: active.filter((p) => p.useBusinessHours).length,
  };
}

/**
 * The order the table renders in.
 *
 * Not a match order — there isn't one — so the sort is for reading: the policy
 * that governs the most tickets first, then the rest alphabetically, then
 * everything switched off. Sorting by `createdAt` would put the newest
 * experiment above the one actually running the service.
 */
export function readingOrder(policies: SlaPolicy[]): SlaPolicy[] {
  const rank = (p: SlaPolicy) => (p.isActive ? (p.isDefault ? 0 : 1) : 2);
  return [...policies].sort(
    (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name),
  );
}
